/**
 * Generate and silently print AvePark parent/guardian declarations as HTML.
 * Fetches declaration texts from the translations API (category: declarations).
 * Fallback: requested lang → en → pl.
 */

const API = process.env.REACT_APP_BACKEND_URL;

let cachedTranslations = null;
let inflightFetch = null;

async function fetchDeclarationTexts() {
  if (cachedTranslations) return cachedTranslations;
  if (inflightFetch) return inflightFetch;
  inflightFetch = (async () => {
    try {
      const res = await fetch(`${API}/api/translations?category=declarations`);
      if (res.ok) {
        const data = await res.json();
        const map = {};
        data.forEach(item => {
          map[item.key] = item.translations || {};
        });
        cachedTranslations = map;
        return map;
      }
    } catch (e) {
      console.error('Failed to fetch declaration translations:', e);
    }
    return {};
  })();
  return inflightFetch;
}

/** Pre-warm the translation cache (call on mount to avoid network delay on print). */
export function prefetchDeclarationTexts() {
  fetchDeclarationTexts().catch(() => {});
}

function t(texts, key, lang) {
  const entry = texts[key] || {};
  return entry[lang] || entry.en || entry.pl || '';
}

/**
 * @param {Array} declarations
 * @param {string} lang - current language code
 */
export async function printDeclarations(declarations, lang = 'pl') {
  const texts = await fetchDeclarationTexts();
  const useLang = lang || 'pl';

  // Combine all declarations into ONE document with page breaks
  const pages = declarations.map((decl, i) => {
    const html = buildDeclarationHtml(decl, texts, useLang);
    // Add page break after each except the last
    return i < declarations.length - 1
      ? html + '<div style="page-break-after:always;"></div>'
      : html;
  });

  const fullHtml = buildFullHtml(pages.join('\n'));
  printViaIframe(fullHtml);
}

export function clearDeclarationCache() {
  cachedTranslations = null;
}

/**
 * Print the provided HTML via a hidden iframe using srcdoc (atomic, no race).
 * onload is attached BEFORE the iframe is inserted into the DOM so we never
 * miss the load event, and srcdoc avoids the document.write/open/close trap
 * that previously caused React to crash with "Uncaught runtime error".
 */
function printViaIframe(html) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  // Tell PostHog / session recorders to ignore this iframe entirely.
  iframe.className = 'ph-no-capture';
  iframe.setAttribute('data-ph-no-capture', 'true');
  iframe.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;border:0;opacity:0;pointer-events:none;z-index:-1;';

  // IMPORTANT: append FIRST so iframe.contentDocument is a valid same-origin
  // about:blank document. Only then can we safely open/write/close.
  // We intentionally avoid `srcdoc` because it creates an `about:srcdoc`
  // browsing context that PostHog's session-recorder mis-treats as
  // cross-origin and crashes with SecurityError on removeEventListener.
  document.body.appendChild(iframe);

  let printed = false;
  const triggerPrint = () => {
    if (printed) return;
    printed = true;
    try {
      const win = iframe.contentWindow;
      if (!win) return;
      win.focus();
      win.print();
    } catch (e) {
      console.error('Print failed:', e);
    }
    setTimeout(() => {
      try { iframe.parentNode && iframe.parentNode.removeChild(iframe); } catch { /* noop */ }
    }, 15000);
  };

  try {
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
  } catch (e) {
    console.error('Failed to write iframe document:', e);
    return;
  }

  // document.write is synchronous → DOM is ready now. Give the browser one
  // tick to compute layout/fonts inside the iframe, then fire print ASAP.
  setTimeout(triggerPrint, 80);
  // Safety fallback (should never be needed but keeps us robust).
  setTimeout(() => { if (!printed) triggerPrint(); }, 800);
}

function buildFullHtml(bodyContent) {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Oswiadczenie</title>
<style>
  @page { size: A4 portrait; margin: 8mm 12mm 6mm 12mm; }
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: 'Segoe UI', Arial, Helvetica, sans-serif; color: #1a1a2e; font-size: 9pt; line-height: 1.3; }
  .page { width:100%; }
  .hdr { text-align:center; margin-bottom:6px; }
  .hdr h1 { font-family:Georgia,serif; font-size:20pt; font-weight:900; letter-spacing:2px; margin:0; }
  .hdr .sub { font-size:6.5pt; letter-spacing:3px; text-transform:uppercase; color:#666; margin-top:-2px; }
  .mt { font-size:12pt; font-weight:800; text-transform:uppercase; margin:6px 0 6px; }
  .fr { display:flex; gap:14px; margin-bottom:4px; }
  .fg { flex:1; }
  .fl { font-size:7.5pt; font-weight:600; margin-bottom:1px; }
  .fs { font-size:6pt; color:#999; font-style:italic; }
  .fb { border:1px solid #999; padding:4px 6px; min-height:22px; font-size:10pt; font-weight:700; color:#1a1a4e; }
  .sb { border:1px solid #aaa; padding:8px 10px; margin:6px 0; }
  .st { text-align:center; font-size:11pt; font-weight:700; margin-bottom:6px; }
  .ct { font-size:9pt; margin-bottom:3px; }
  .dr { display:flex; gap:2px; align-items:center; }
  .db { width:17px; height:18px; border:1px solid #999; text-align:center; font-size:9.5pt; font-weight:700; display:inline-flex; align-items:center; justify-content:center; color:#1a1a4e; }
  .ds { font-size:12pt; font-weight:700; padding:0 1px; }
  .dl { font-size:6.5pt; color:#777; margin-bottom:1px; }
  .cr { display:inline-flex; align-items:center; gap:3px; }
  .cb { width:12px; height:12px; border:1px solid #999; display:inline-flex; align-items:center; justify-content:center; font-size:10pt; font-weight:900; color:#1a1a4e; }
  .pts p { margin-bottom:2px; font-size:8.5pt; }
  .sr { display:flex; gap:14px; margin-top:6px; align-items:flex-end; }
  .sx { flex:1; }
  .sl { border:1px solid #999; min-height:24px; padding:2px 6px; }
  .slb { font-size:6.5pt; text-align:center; color:#777; margin-top:1px; }
  .ss { margin-top:8px; }
  .sst { font-size:9pt; font-weight:700; margin-bottom:3px; }
  .ssx { font-size:7.5pt; line-height:1.25; margin-bottom:4px; color:#333; }
  .ftr { text-align:center; margin-top:6px; }
  .ftr h2 { font-family:Georgia,serif; font-size:14pt; font-weight:900; letter-spacing:2px; }
  .ftr .sub { font-size:6pt; letter-spacing:3px; text-transform:uppercase; color:#666; }
  @media print { body { -webkit-print-color-adjust:exact; print-color-adjust:exact; } }
</style></head><body>${bodyContent}</body></html>`;
}

function fmt(iso) {
  if (!iso) return { dd:'', mm:'', yyyy:'' };
  const d = new Date(iso);
  return { dd:String(d.getDate()).padStart(2,'0'), mm:String(d.getMonth()+1).padStart(2,'0'), yyyy:String(d.getFullYear()) };
}
function todayFmt() { return fmt(new Date().toISOString()); }
function dateBoxes(o) {
  const {dd,mm,yyyy} = o;
  return `<div class="dr"><div class="db">${dd[0]||''}</div><div class="db">${dd[1]||''}</div><span class="ds">/</span><div class="db">${mm[0]||''}</div><div class="db">${mm[1]||''}</div><span class="ds">/</span><div class="db">${yyyy[0]||''}</div><div class="db">${yyyy[1]||''}</div><div class="db">${yyyy[2]||''}</div><div class="db">${yyyy[3]||''}</div></div>`;
}

function buildDeclarationHtml({ parentFirstName, parentLastName, childFirstName, childLastName, childBirthDate, presence, isSelf }, texts, lang) {
  const birth = fmt(childBirthDate);
  const empty = { dd:'', mm:'', yyyy:'' };
  // For solo minor: leave signing dates empty (parent fills them later)
  const td = isSelf ? empty : todayFmt();
  const wo = presence === 'without' ? 'X' : '';
  const wi = presence === 'with' ? 'X' : '';

  const g = (key) => t(texts, key, lang);

  return `<div class="page">
  <div class="hdr"><h1>AVE/PARK</h1><div class="sub">{warsaw indoor skatepark}</div></div>
  <div class="mt">${g('declaration.title')}</div>
  <div class="fr">
    <div class="fg"><div class="fl">${g('declaration.parentFirstName')}</div><div class="fs">${g('declaration.uppercase')}</div><div class="fb">${parentFirstName ? parentFirstName.toUpperCase() : ''}</div></div>
    <div class="fg"><div class="fl">${g('declaration.parentLastName')}</div><div class="fs">${g('declaration.uppercase')}</div><div class="fb">${parentLastName ? parentLastName.toUpperCase() : ''}</div></div>
  </div>
  <div class="sb">
    <div class="st">${g('declaration.sectionTitle')}</div>
    <p class="ct">${g('declaration.consentIntro')}</p>
    <div class="fr">
      <div class="fg"><div class="fl">${g('declaration.childFirstName')}</div><div class="fs">${g('declaration.uppercase')}</div><div class="fb">${childFirstName.toUpperCase()}</div></div>
      <div class="fg"><div class="fl">${g('declaration.childLastName')}</div><div class="fs">${g('declaration.uppercase')}</div><div class="fb">${childLastName.toUpperCase()}</div></div>
    </div>
    <div style="margin-bottom:4px;"><div class="dl">${g('declaration.childBirthDate')}</div>${dateBoxes(birth)}</div>
    <p class="ct">${g('declaration.venueUsage')}&nbsp;&nbsp;<span class="cr"><span class="cb">${wo}</span> ${g('declaration.withoutPresence')}</span>&nbsp;&nbsp;<span class="cr"><span class="cb">${wi}</span> ${g('declaration.withPresence')}</span></p>
    <p class="ct" style="margin-top:4px;">${g('declaration.alsoState')}</p>
    <div class="pts">
      <p>1. ${g('declaration.point1')}</p>
      <p>2. ${g('declaration.point2')}</p>
      <p>3. ${g('declaration.point3')}</p>
      <p>4. ${g('declaration.point4')}</p>
    </div>
    <div class="sr">
      <div class="sx"><div class="dl">${g('declaration.date')}</div>${dateBoxes(td)}</div>
      <div class="sx"><div class="sl"></div><div class="slb">${g('declaration.signatureParent')}</div></div>
    </div>
  </div>
  <div class="ss">
    <div class="sst">${g('declaration.imageConsentTitle')}</div>
    <p class="ssx">${g('declaration.imageConsent')}</p>
    <div class="sr">
      <div class="sx"><div class="dl">${g('declaration.date')}</div>${dateBoxes(td)}</div>
      <div class="sx"><div class="sl"></div><div class="slb">${g('declaration.signatureShort')}</div></div>
    </div>
  </div>
  <div class="ss">
    <div class="sst">${g('declaration.gdprTitle')}</div>
    <p class="ssx">${g('declaration.gdprConsent')}</p>
    <div style="display:flex;gap:14px;align-items:flex-end;">
      <div class="sx" style="flex:0 0 auto;"><div class="sl" style="min-width:180px;"></div><div class="slb">${g('declaration.signatureShort')}</div></div>
    </div>
  </div>
  <div class="ftr"><h2>AVE/PARK</h2><div class="sub">{warsaw indoor skatepark}</div></div>
</div>`;
}
